import { IcoCalendar, IcoClock } from '@components/icons';
import { useEffect, useState } from 'preact/hooks';
import type { PaneProps } from '../../types';
import { ZoomIntegration } from '../../cal-integrations/zoom-integration';
import { GoogleCalendarIntegration } from '../../cal-integrations/external-calendars';
import { DurationInput } from '../../cal-edit-event-types/duration-input';
import { EventTypeRow } from 'server/types/cal-schema';
import { useBasicAutosaver } from '@components/autosaver';
import { rpx } from 'client/lib/rpx-client';
import { showError } from '@components/app-error';
import { Case } from '@components/conditional';
import { BtnPrimary } from '@components/buttons';

function EventTypeSelector(props: { eventType: EventTypeRow; hasZoom: boolean }) {
  const [isEditing, setIsEditing] = useState(false);
  const [eventType, setEventType] = useState(props.eventType);

  useBasicAutosaver(eventType, async (val) => {
    try {
      await rpx.ruzcalEventTypes.saveEventType({
        id: eventType.id,
        availabilityId: eventType.availabilityId,
        categoryId: eventType.categoryId!,
        description: eventType.description,
        urlSuffix: eventType.urlSuffix,
        color: eventType.color,
        // Only those fields are changing actually
        name: val.name,
        duration: val.duration,
        location: val.location,
      });
    } catch (err) {
      showError(err);
    }
  });

  useEffect(() => {
    setEventType((e) => ({
      ...e,
      location: props.hasZoom ? 'zoom' : 'jitsi',
    }));
  }, [props.hasZoom]);

  return (
    <div class="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      <div class="flex items-center justify-between mb-3">
        <h3 class="text-sm font-semibold text-gray-700">Session Details</h3>
        <button
          onClick={() => setIsEditing(!isEditing)}
          class="text-sm text-blue-600 hover:text-blue-700 font-medium"
        >
          {isEditing ? 'Done' : 'Edit'}
        </button>
      </div>

      {!isEditing && (
        <div class="flex items-center justify-between bg-gray-50 rounded-md px-3 py-2">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <IcoCalendar class="w-6 h-6 text-blue-600" />
            </div>
            <p class="text-gray-900 font-semibold text-sm">{eventType.name}</p>
          </div>
          <div class="flex items-center gap-2">
            <div class="flex items-center gap-1.5 bg-white px-2.5 py-1 rounded-md border border-gray-200">
              <IcoClock class="w-4 h-4 text-gray-400" />
              <span class="text-gray-700 text-sm font-medium">{eventType.duration} min</span>
            </div>
          </div>
        </div>
      )}
      {isEditing && (
        <div class="flex flex-col gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={eventType.name}
              onInput={(e) =>
                setEventType({ ...eventType, name: (e.target as HTMLInputElement).value })
              }
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter event title"
            />
          </div>
          <DurationInput
            value={eventType.duration}
            propName="duration"
            onChange={(duration) => setEventType({ ...eventType, duration })}
          />
        </div>
      )}
    </div>
  );
}

export function CalendarStep({ eventType, integrations, onNext }: PaneProps) {
  return (
    <div class="flex flex-col h-full py-4">
      <div class="flex-1 flex flex-col gap-6">
        <div class="text-center">
          <div class="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <IcoCalendar class="w-8 h-8 text-white" />
          </div>
          <h1 class="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-3">
            Configure Your Calendar
          </h1>
          <p class="text-lg text-gray-600 leading-relaxed">
            Define the type and duration of meetings you'll offer to your clients
          </p>
        </div>

        <div class="flex flex-col gap-6">
          {eventType && (
            <EventTypeSelector eventType={eventType} hasZoom={!!integrations?.hasZoom} />
          )}
          <div class="relative">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-gray-200"></div>
            </div>
            <div class="relative flex justify-center">
              <span class="bg-white px-3 text-sm text-gray-500">Optional Integrations</span>
            </div>
          </div>
          <p class="text-center text-sm text-gray-600">
            Connect Google Calendar for availability sync and Zoom to host video meetings
          </p>
          <div class="flex flex-col gap-3">
            {integrations && (
              <GoogleCalendarIntegration hasGoogle={integrations.hasGoogle} isForSignup />
            )}
            {integrations && (
              <ZoomIntegration
                hasAccount={integrations.hasZoom}
                oauth={integrations.zoomOauth}
                isForSignup
              />
            )}
          </div>
        </div>

        <div class="text-center mt-4">
          <Case
            when={integrations?.hasGoogle}
            fallback={
              <button onClick={onNext} class="text-sm text-gray-500 hover:text-gray-700 underline">
                Skip for now
              </button>
            }
          >
            <BtnPrimary onClick={onNext} class="w-full rounded-md">
              Continue
            </BtnPrimary>
          </Case>
        </div>
      </div>
    </div>
  );
}
